/**
 * Sync Manager Service
 *
 * Handles background synchronization of offline mutations when the app
 * comes back online. Uses exponential backoff for retries and provides
 * real-time sync status updates.
 */

import { syncQueue, userDataCache, apiCache } from './db';

export type SyncStatus = 'idle' | 'syncing' | 'error' | 'offline';

export interface SyncProgress {
  status: SyncStatus;
  total: number;
  completed: number;
  failed: number;
  currentItem?: string;
}

type SyncListener = (progress: SyncProgress) => void;

class SyncManager {
  private isOnline: boolean = navigator.onLine;
  private isSyncing: boolean = false;
  private listeners: Set<SyncListener> = new Set();
  private syncInterval: number | null = null;
  private progress: SyncProgress = {
    status: 'idle',
    total: 0,
    completed: 0,
    failed: 0,
  };

  constructor() {
    // Listen for online/offline events
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);

    // Check initial state
    this.updateOnlineStatus();

    // Start periodic sync check
    this.startPeriodicSync();

    // Register for background sync if available
    this.registerBackgroundSync();
  }

  private handleOnline = () => {
    console.log('[SyncManager] Network online');
    this.isOnline = true;
    this.updateProgress({ status: 'idle' });
    // Trigger sync when coming back online
    this.sync();
  };

  private handleOffline = () => {
    console.log('[SyncManager] Network offline');
    this.isOnline = false;
    this.updateProgress({ status: 'offline' });
  };

  private updateOnlineStatus() {
    this.isOnline = navigator.onLine;
    if (!this.isOnline) {
      this.updateProgress({ status: 'offline' });
    }
  }

  private startPeriodicSync() {
    // Check for pending syncs every 30 seconds
    this.syncInterval = window.setInterval(() => {
      if (this.isOnline && !this.isSyncing) {
        this.checkPendingSync();
      }
    }, 30000);
  }

  private async checkPendingSync() {
    const pendingCount = await syncQueue.count();
    if (pendingCount > 0) {
      console.log(`[SyncManager] Found ${pendingCount} pending sync items`);
      this.sync();
    }
  }

  private async registerBackgroundSync() {
    if ('serviceWorker' in navigator && 'sync' in ServiceWorkerRegistration.prototype) {
      try {
        const registration = await navigator.serviceWorker.ready;
        // @ts-expect-error - Background sync API types
        await registration.sync.register('offline-sync');
        console.log('[SyncManager] Background sync registered');
      } catch (error) {
        console.warn('[SyncManager] Background sync registration failed:', error);
      }
    }
  }

  private updateProgress(updates: Partial<SyncProgress>) {
    this.progress = { ...this.progress, ...updates };
    this.notifyListeners();
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener(this.progress));
  }

  /**
   * Subscribe to sync progress updates
   */
  subscribe(listener: SyncListener): () => void {
    this.listeners.add(listener);
    // Immediately notify with current status
    listener(this.progress);

    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Get current sync status
   */
  getStatus(): SyncProgress {
    return this.progress;
  }

  /**
   * Check if we're currently online
   */
  get online(): boolean {
    return this.isOnline;
  }

  /**
   * Queue an API request for offline sync
   */
  async queueRequest(
    endpoint: string,
    method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    payload?: unknown,
    options?: {
      priority?: number;
      headers?: Record<string, string>;
    }
  ): Promise<number> {
    const type = method === 'POST' ? 'create' : method === 'DELETE' ? 'delete' : 'update';

    const id = await syncQueue.add({
      type,
      endpoint,
      method,
      payload,
      headers: options?.headers,
      priority: options?.priority ?? 1,
    });

    console.log(`[SyncManager] Queued ${method} request to ${endpoint} (ID: ${id})`);

    // Try to sync immediately if online
    if (this.isOnline) {
      this.sync();
    }

    return id;
  }

  /**
   * Perform synchronization of pending items
   */
  async sync(): Promise<void> {
    if (!this.isOnline) {
      console.log('[SyncManager] Cannot sync while offline');
      return;
    }

    if (this.isSyncing) {
      console.log('[SyncManager] Sync already in progress');
      return;
    }

    this.isSyncing = true;
    const pendingItems = await syncQueue.getPending();

    if (pendingItems.length === 0) {
      this.isSyncing = false;
      this.updateProgress({ status: 'idle', total: 0, completed: 0, failed: 0 });
      return;
    }

    console.log(`[SyncManager] Starting sync of ${pendingItems.length} items`);
    this.updateProgress({
      status: 'syncing',
      total: pendingItems.length,
      completed: 0,
      failed: 0,
    });

    let completed = 0;
    let failed = 0;

    for (const item of pendingItems) {
      if (!this.isOnline) {
        console.log('[SyncManager] Went offline during sync, pausing');
        break;
      }

      this.updateProgress({
        currentItem: `${item.method} ${item.endpoint}`,
      });

      try {
        await this.executeRequest(item);
        await syncQueue.remove(item.id!);
        completed++;
        this.updateProgress({ completed });
        console.log(`[SyncManager] Successfully synced item ${item.id}`);
      } catch (error) {
        failed++;
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        await syncQueue.updateRetry(item.id!, errorMessage);
        console.error(`[SyncManager] Failed to sync item ${item.id}:`, error);
        this.updateProgress({ failed });
      }

      // Small delay between requests to avoid overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    this.isSyncing = false;

    if (failed > 0) {
      this.updateProgress({ status: 'error', currentItem: undefined });
    } else {
      this.updateProgress({ status: 'idle', currentItem: undefined });
    }

    console.log(`[SyncManager] Sync completed: ${completed} succeeded, ${failed} failed`);

    // Sync dirty user data
    await this.syncDirtyData();
  }

  private async executeRequest(item: {
    endpoint: string;
    method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    payload?: unknown;
    headers?: Record<string, string>;
  }): Promise<Response> {
    const token = localStorage.getItem('accessToken');

    const response = await fetch(item.endpoint, {
      method: item.method,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...item.headers,
      },
      body: item.payload ? JSON.stringify(item.payload) : undefined,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response;
  }

  private async syncDirtyData(): Promise<void> {
    const dirtyItems = await userDataCache.getDirty();

    if (dirtyItems.length === 0) return;

    console.log(`[SyncManager] Syncing ${dirtyItems.length} dirty data items`);

    for (const item of dirtyItems) {
      try {
        // Determine endpoint based on type
        let endpoint = '';
        switch (item.type) {
          case 'pfile':
            endpoint = `/api/v1/staff-pfiles/${item.id}`;
            break;
          case 'document':
            endpoint = `/api/v1/documents/${item.id}`;
            break;
          case 'user':
            endpoint = `/api/v1/users/${item.id}`;
            break;
          default:
            continue;
        }

        await this.executeRequest({
          endpoint,
          method: 'PUT',
          payload: item.data,
        });

        await userDataCache.markSynced(item.id);
        console.log(`[SyncManager] Synced dirty ${item.type} ${item.id}`);
      } catch (error) {
        console.error(`[SyncManager] Failed to sync dirty ${item.type} ${item.id}:`, error);
      }
    }
  }

  /**
   * Clear all sync data (for logout)
   */
  async clearAll(): Promise<void> {
    await syncQueue.clear();
    await userDataCache.clear();
    await apiCache.clear();
    this.updateProgress({ status: 'idle', total: 0, completed: 0, failed: 0 });
  }

  /**
   * Force retry all failed items
   */
  async retryFailed(): Promise<void> {
    // This is handled by the sync() method automatically
    await this.sync();
  }

  /**
   * Cleanup on app unmount
   */
  destroy() {
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }
  }
}

// Export singleton instance
export const syncManager = new SyncManager();

// Export type for hook usage
export type { SyncManager };
